import React, { useMemo } from "react";

import { BlogPostCommentCreateFormFactory } from "@/mk2/apps/blogs/components/BlogPostCommentCreateForm";
import { MessageFormFactory } from "@/mk2/apps/blogs/components/MessageForm";
import { CommentsDesign } from "@/mk2/apps/blogs/constants";
import { CommentFormData } from "@/mk2/apps/forum/components/CommentCreateEditForm";
import { UserEntity } from "@/mk2/schemas";
import {
    BLOGS_ADD_NEW_COMMENT_PLACEHOLDER,
    SEND_COMMENT_FORUM,
} from "@/mk/autogenerated/translations/BlogCommentAdd.c0230d59126a47146a5ce6b0607717f8";

import styles from "./BlogCommentAdd.mscss";

interface OwnProps {
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
    canAdd: boolean;
    canShowForm: boolean;
    isAuthenticated: boolean;
    hasBlueToolbar?: boolean;
    hasRating?: boolean;
    createFormName: string;
    design: CommentsDesign;

    onSetTextareaRef?(ref: HTMLTextAreaElement);
    onCreate(commentFd: CommentFormData);
    onCreatePendingAction(commentFd: CommentFormData);
    onBlur?();
    onFocus?();
}

const BlogCommentAdd: React.FunctionComponent<OwnProps> = ({
    canAdd,
    canShowForm,
    isAuthenticated: isAuth,
    requestUser,
    requestUserPermissions,
    design,
    isMobile,
    hasBlueToolbar,
    hasRating,
    createFormName,
    onBlur,
    onFocus,
    onCreate,
    onSetTextareaRef,
    onCreatePendingAction,
}) => {
    const BlogPostCommentCreateForm = useMemo(
        () => BlogPostCommentCreateFormFactory(createFormName),
        [createFormName],
    );
    const MessageForm = useMemo(() => MessageFormFactory(createFormName), [createFormName]);
    const initialValues: CommentFormData = useMemo(
        () => ({ text: "", photos: [], sticker: null }),
        [createFormName],
    );

    if (canAdd) {
        // Authenticated user — full form (photos, admin, rating)
        return design === CommentsDesign.Normal ? (
            <BlogPostCommentCreateForm
                initialValues={initialValues}
                isMobile={isMobile}
                hasBlueToolbar={hasBlueToolbar}
                hasRating={hasRating}
                onBlur={onBlur}
                onFocus={onFocus}
                onSave={onCreate}
                requestUserPermissions={requestUserPermissions}
                textareaRef={onSetTextareaRef}
            />
        ) : (
            <div className={styles.BlogCommentAdd__messageForm}>
                <MessageForm
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    initialValues={initialValues}
                    onSend={onCreate}
                    textareaRef={onSetTextareaRef}
                />
            </div>
        );
    } else if (canShowForm && !isAuth) {
        // Unauthenticated user — form without photos/admin/rating
        return design === CommentsDesign.Normal ? (
            <BlogPostCommentCreateForm
                initialValues={initialValues}
                isMobile={isMobile}
                hasBlueToolbar={hasBlueToolbar}
                isUnauthenticatedUser
                onBlur={onBlur}
                onFocus={onFocus}
                onSave={onCreatePendingAction}
                requestUserPermissions={requestUserPermissions}
                textareaRef={onSetTextareaRef}
            />
        ) : (
            <div className={styles.BlogCommentAdd__messageForm}>
                <MessageForm
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    isUnauthenticatedUser
                    initialValues={initialValues}
                    onSend={onCreatePendingAction}
                    textareaRef={onSetTextareaRef}
                />
            </div>
        );
    } else {
        return null;
    }
};

BlogCommentAdd.displayName = "BlogCommentAdd";

export default React.memo<OwnProps>(BlogCommentAdd);
